import React, { useState, useEffect, useMemo } from 'react';
import { Search, Filter, Clock, Package, TrendingUp, AlertTriangle, CheckCircle, XCircle, ExternalLink, FileText, Eye, EyeOff, ChevronDown, ChevronRight, Plus, Minus, Star } from 'lucide-react';
import { useTable, useSortBy, useGlobalFilter, useExpanded } from 'react-table';
import { useToast } from '../hooks/use-toast';

const API_URL = import.meta.env.VITE_API_URL;

// Utility functions
const getPriorityLevel = (daysOld, units) => {
  if (daysOld >= 20) return 'critical';
  if (daysOld >= 10) return 'high';
  if (daysOld >= 5 || units >= 500) return 'medium';
  return 'low';
};

const getPriorityColor = (priority) => {
  const colors = {
    critical: 'bg-red-100 text-red-800 border-red-200',
    high: 'bg-orange-100 text-orange-800 border-orange-200',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    low: 'bg-green-100 text-green-800 border-green-200'
  };
  return colors[priority] || colors.low;
};

const Tasks = () => {
  const [outbounds, setOutbounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all'); // 'all', 'us', 'ca'
  const [daysRange, setDaysRange] = useState('120');
  const [selectedOutbound, setSelectedOutbound] = useState(null);
  const [showIgnored, setShowIgnored] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'priority'
  const [priorityList, setPriorityList] = useState([]);
  const [completedItems, setCompletedItems] = useState([]);
  const [priorityLoading, setPriorityLoading] = useState(false);
  const [sizeFilters, setSizeFilters] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    fetchOutbounds();
    fetchPriorityList();
  }, [daysRange]);

  const fetchPriorityList = async () => {
    try {
      setPriorityLoading(true);
      const response = await fetch(`${API_URL}/api/priority-list`);
      if (!response.ok) throw new Error('Failed to fetch priority list');
      const data = await response.json();
      setPriorityList(data.data || []);
      setCompletedItems(data.data?.filter(item => item.COMPLETED === 1).map(item => item.ID) || []);
    } catch (error) {
      console.error('Error fetching priority list:', error);
      toast({
        title: "Error",
        description: "Failed to fetch priority list",
        variant: "destructive",
      });
    } finally {
      setPriorityLoading(false);
    }
  };

  const fetchOutbounds = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/pending-outbounds?days=${daysRange}`);
      if (!response.ok) throw new Error('Failed to fetch outbounds');
      const data = await response.json();
      setOutbounds(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch shipments",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const currentData = useMemo(() => {
    if (activeTab === 'priority') {
      return priorityList;
    }
    return outbounds;
  }, [activeTab, outbounds, priorityList]);

  const getCountryFromClient = (clientName) => {
    if (!clientName) return 'unknown';
    const name = clientName.toLowerCase();
    if (name.includes('(us)')) return 'us';
    if (name.includes('(ca)')) return 'ca';
    return 'unknown';
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCountryFilter('all');
    setDaysRange('120');
    setSizeFilters([]);
    setShowIgnored(false);
  };

  const filteredOutbounds = useMemo(() => {
    let filtered = outbounds;

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(outbound => outbound.STATUS === statusFilter);
    }

    // Country filter
    if (countryFilter !== 'all') {
      filtered = filtered.filter(outbound => {
        const country = getCountryFromClient(outbound.name);
        return country === countryFilter;
      });
    }

    // Size filters
    if (sizeFilters.length > 0) {
      filtered = filtered.filter(outbound => {
        const units = parseInt(outbound.ITEM_COUNT || 0);
        return sizeFilters.some(filter => {
          switch (filter) {
            case 'tiny': return units < 100;
            case 'small': return units >= 100 && units < 300;
            case 'mid': return units >= 300 && units < 1000;
            case 'big': return units >= 1000;
            default: return false;
          }
        });
      });
    }

    // Ignored filter
    if (!showIgnored) {
      filtered = filtered.filter(outbound => !outbound.ignored);
    }

    return filtered;
  }, [outbounds, statusFilter, countryFilter, sizeFilters, showIgnored]);

  const stats = useMemo(() => {
    const visible = filteredOutbounds.filter(o => !o.ignored);
    return {
      total: visible.length,
      critical: visible.filter(o => getPriorityLevel(parseInt(o.BusinessDaysOld || 0), parseInt(o.ITEM_COUNT || 0)) === 'critical').length,
      high: visible.filter(o => getPriorityLevel(parseInt(o.BusinessDaysOld || 0), parseInt(o.ITEM_COUNT || 0)) === 'high').length,
      totalUnits: visible.reduce((sum, o) => sum + parseInt(o.ITEM_COUNT || 0), 0),
      avgDays: visible.length ? Math.round(visible.reduce((sum, o) => sum + parseInt(o.BusinessDaysOld || 0), 0) / visible.length) : 0
    };
  }, [filteredOutbounds]);

  const handleIgnoreOutbound = async (outboundId) => {
    try {
      const response = await fetch(`${API_URL}/api/outbound-tasks/ignore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outboundId, ignore: 1 })
      });
      
      if (!response.ok) throw new Error('Failed to ignore outbound');
      
      setOutbounds(prev => prev.map(o => 
        o.ID === outboundId ? { ...o, ignored: true } : o
      ));
      
      toast({
        title: "Success",
        description: "Shipment marked as irrelevant",
        variant: "default",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to ignore shipment",
        variant: "destructive",
      });
    }
  };

  const handleUpdateNotes = async (outboundId, notes) => {
    try {
      // Optimistically update UI immediately
      const updateData = { NOTES: notes, notes, updated_at: new Date().toISOString() };
      setOutbounds(prev => prev.map(o => 
        o.ID === outboundId ? { ...o, ...updateData } : o
      ));
      setPriorityList(prev => prev.map(o => 
        o.ID === outboundId ? { ...o, ...updateData } : o
      ));
      
      // Send to server in background
      const response = await fetch(`${API_URL}/api/outbound-tasks/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outboundId, notes })
      });
      
      if (!response.ok) {
        // Revert on failure
        setOutbounds(prev => prev.map(o => 
          o.ID === outboundId ? { ...o, NOTES: '', notes: '' } : o
        ));
        setPriorityList(prev => prev.map(o => 
          o.ID === outboundId ? { ...o, NOTES: '', notes: '' } : o
        ));
        throw new Error('Failed to save notes');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save notes",
        variant: "destructive",
      });
    }
  };

  const addToPriorityList = async (outbound) => {
    if (priorityList.find(item => item.ID === outbound.ID)) {
      return; // Silent return for speed
    }

    try {
      // Optimistically add to UI immediately
      setPriorityList(prev => [...prev, { ...outbound, PRIORITY: 1, COMPLETED: 0 }]);
      
      const response = await fetch(`${API_URL}/api/priority-list/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shipment_id: outbound.ID })
      });
      
      if (!response.ok) {
        // Revert on failure
        setPriorityList(prev => prev.filter(item => item.ID !== outbound.ID));
        throw new Error('Failed to add to priority list');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add to priority list",
        variant: "destructive",
      });
    }
  };

  const removeFromPriorityList = async (outboundId) => {
    try {
      // Optimistically remove from UI immediately
      const removedItem = priorityList.find(item => item.ID === outboundId);
      setPriorityList(prev => prev.filter(item => item.ID !== outboundId));
      
      const response = await fetch(`${API_URL}/api/priority-list/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shipment_id: outboundId })
      });
      
      if (!response.ok) {
        // Revert on failure
        if (removedItem) {
          setPriorityList(prev => [...prev, removedItem]);
        }
        throw new Error('Failed to remove from priority list');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove from priority list",
        variant: "destructive",
      });
    }
  };

  const toggleCompleted = async (outboundId) => {
    const isCompleted = completedItems.includes(outboundId);
    
    try {
      // Optimistically update UI immediately
      if (isCompleted) {
        setCompletedItems(prev => prev.filter(id => id !== outboundId));
      } else {
        setCompletedItems(prev => [...prev, outboundId]);
      }
      
      const response = await fetch(`${API_URL}/api/priority-list/toggle-completed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          shipment_id: outboundId
        })
      });
      
      if (!response.ok) {
        // Revert on failure
        if (isCompleted) {
          setCompletedItems(prev => [...prev, outboundId]);
        } else {
          setCompletedItems(prev => prev.filter(id => id !== outboundId));
        }
        throw new Error('Failed to update completion status');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update completion status",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Loading shipments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Header with Tabs */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        {/* Tab Navigation */}
        <div className="px-2 sm:px-4 pt-2">
          <div className="flex space-x-1 overflow-x-auto">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'all'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              All Shipments ({outbounds.length})
            </button>
            <button
              onClick={() => setActiveTab('priority')}
              className={`px-4 py-2 text-sm font-medium flex items-center space-x-1 ${
                activeTab === 'priority'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <Star className="h-4 w-4" />
              <span>Priority List ({priorityList.length})</span>
            </button>
          </div>
        </div>

        {/* Stats and Controls */}
        <div className="px-2 sm:px-4 py-2">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-6">
              <h1 className="text-base sm:text-lg font-semibold text-gray-900">
                {activeTab === 'all' ? 'All Pending Shipments' : 'Priority List'}
              </h1>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm">
                <span className="flex items-center space-x-1">
                  <Package className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-blue-900">{stats.total}</span>
                  <span className="text-gray-600">total</span>
                </span>
                <span className="flex items-center space-x-1">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <span className="font-medium text-red-900">{stats.critical}</span>
                  <span className="text-gray-600">critical</span>
                </span>
                <span className="flex items-center space-x-1">
                  <TrendingUp className="h-4 w-4 text-orange-600" />
                  <span className="font-medium text-orange-900">{stats.high}</span>
                  <span className="text-gray-600">high</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Package className="h-4 w-4 text-green-600" />
                  <span className="font-medium text-green-900">{stats.totalUnits.toLocaleString()}</span>
                  <span className="text-gray-600">units</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Clock className="h-4 w-4 text-gray-600" />
                  <span className="font-medium text-gray-900">{stats.avgDays}</span>
                  <span className="text-gray-600">avg days</span>
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <Clock className="h-3 w-3" />
              <span>Updated: {new Date().toLocaleTimeString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="bg-white border-b border-gray-200 px-2 sm:px-6 py-3 sm:py-4 flex-shrink-0">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full lg:w-auto">
            {/* Search */}
            <div className="relative flex-grow sm:flex-grow-0">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search shipments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm w-full sm:w-64"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm min-w-0"
            >
              <option value="all">All Status</option>
              <option value="planning">Planning</option>
              <option value="planned">Planned</option>
              <option value="open">Open</option>
            </select>

            {/* Country Filter */}
            <select
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm min-w-0"
            >
              <option value="all">All Countries</option>
              <option value="us">US Clients</option>
              <option value="ca">CA Clients</option>
            </select>

            {/* Days Range */}
            <select
              value={daysRange}
              onChange={(e) => setDaysRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm min-w-0"
            >
              <option value="30">Last 30 days</option>
              <option value="60">Last 60 days</option>
              <option value="90">Last 90 days</option>
              <option value="120">Last 120 days</option>
              <option value="180">Last 180 days</option>
              <option value="365">Last year</option>
            </select>

            {/* Size Filters */}
            <div className="flex flex-wrap gap-1">
              {[
                { key: 'tiny', label: 'Tiny', color: 'bg-gray-500' },
                { key: 'small', label: 'Small', color: 'bg-blue-500' },
                { key: 'mid', label: 'Midsize', color: 'bg-orange-500' },
                { key: 'big', label: 'Large', color: 'bg-red-500' }
              ].map(filter => (
                <button
                  key={filter.key}
                  onClick={() => {
                    setSizeFilters(prev => 
                      prev.includes(filter.key) 
                        ? prev.filter(f => f !== filter.key)
                        : [...prev, filter.key]
                    );
                  }}
                  className={`px-2 py-1 text-xs rounded border ${
                    sizeFilters.includes(filter.key)
                      ? `${filter.color} text-white border-transparent`
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  title={
                    filter.key === 'tiny' ? '<100 units' :
                    filter.key === 'small' ? '100-300 units' :
                    filter.key === 'mid' ? '300-1k units' :
                    '1k+ units'
                  }
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowIgnored(!showIgnored)}
              className={`flex items-center space-x-2 px-3 py-2 text-sm border ${
                showIgnored 
                  ? 'bg-gray-100 border-gray-300 text-gray-700' 
                  : 'bg-white border-gray-300 text-gray-600'
              } hover:bg-gray-50 whitespace-nowrap`}
            >
              {showIgnored ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              <span className="hidden sm:inline">{showIgnored ? 'Hide' : 'Show'} Ignored</span>
            </button>
            
            <button
              onClick={resetFilters}
              className="flex items-center space-x-2 px-3 py-2 text-sm border bg-white border-gray-300 text-gray-600 hover:bg-gray-50 whitespace-nowrap"
            >
              <span>Reset Filters</span>
            </button>
          </div>
        </div>
      </div>

      {/* Shipments Table */}
      <div className="flex-1 overflow-hidden bg-white">
        <ShipmentsTable
          data={filteredOutbounds}
          onIgnore={handleIgnoreOutbound}
          onUpdateNotes={handleUpdateNotes}
          onSelect={setSelectedOutbound}
          selectedId={selectedOutbound?.ID}
          searchTerm={searchTerm}
          activeTab={activeTab}
          priorityList={priorityList}
          completedItems={completedItems}
          onAddToPriority={addToPriorityList}
          onRemoveFromPriority={removeFromPriorityList}
          onToggleCompleted={toggleCompleted}
        />
      </div>
    </div>
  );
};

const ShipmentsTable = ({ 
  data, 
  onIgnore, 
  onUpdateNotes, 
  onSelect, 
  selectedId, 
  searchTerm, 
  activeTab, 
  priorityList, 
  completedItems, 
  onAddToPriority, 
  onRemoveFromPriority, 
  onToggleCompleted 
}) => {
  const [editingNotes, setEditingNotes] = useState({});
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRowExpansion = (rowId) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  const columns = useMemo(() => [
    {
      Header: '',
      id: 'expander',
      width: 40,
      Cell: ({ row }) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleRowExpansion(row.original.ID);
          }}
          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
        >
          {expandedRows[row.original.ID] ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronRight className="h-3 w-3" />
          )}
        </button>
      )
    },
    {
      Header: activeTab === 'priority' ? 'Done' : 'Star',
      id: 'priority_action',
      width: 80,
      Cell: ({ row }) => {
        if (activeTab === 'priority') {
          const isCompleted = completedItems.includes(row.original.ID);
          const daysOld = parseInt(row.original.BusinessDaysOld || 0);
          const isBlackBg = daysOld >= 40;
          
          return (
            <div className="flex items-center justify-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleCompleted(row.original.ID);
                }}
                className={`p-1 rounded ${
                  isCompleted 
                    ? (isBlackBg ? 'text-green-300 hover:bg-red-700' : 'text-green-600 hover:bg-green-100')
                    : (isBlackBg ? 'text-gray-400 hover:bg-red-700' : 'text-gray-400 hover:bg-gray-100')
                } hover:text-green-700`}
              >
                <CheckCircle className="h-4 w-4" />
              </button>
            </div>
          );
        } else {
          const isInPriority = priorityList.some(item => item.ID === row.original.ID);
          const daysOld = parseInt(row.original.BusinessDaysOld || 0);
          const isBlackBg = daysOld >= 40;
          
          return (
            <div className="flex items-center justify-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (isInPriority) {
                    onRemoveFromPriority(row.original.ID);
                  } else {
                    onAddToPriority(row.original);
                  }
                }}
                className={`p-1 rounded ${
                  isInPriority 
                    ? (isBlackBg ? 'text-yellow-300 hover:bg-red-700' : 'text-yellow-500 hover:bg-yellow-100')
                    : (isBlackBg ? 'text-gray-400 hover:bg-red-700' : 'text-gray-400 hover:bg-gray-100')
                } hover:text-yellow-600`}
                title={isInPriority ? 'Remove from priority list' : 'Add to priority list'}
              >
                <Star className="h-4 w-4" fill={isInPriority ? 'currentColor' : 'none'} />
              </button>
            </div>
          );
        }
      }
    },
    {
      Header: 'ID',
      accessor: 'ID',
      width: 80,
      Cell: ({ value }) => (
        <a
          href={`https://app.yyzprep.ca/staff/shipments/outbound/${value}`}
          target="_blank"
          rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 font-mono text-xs font-medium"
          onClick={(e) => e.stopPropagation()}
        >
          #{value}
        </a>
      )
    },
    {
      Header: 'Days',
      accessor: 'BusinessDaysOld',
      width: 80,
      Cell: ({ value }) => {
        const daysOld = parseInt(value || 0);
        return (
          <div className={`inline-flex items-center px-2 py-0.5 text-xs font-bold ${
            daysOld >= 40 ? 'bg-red-800 text-white' :
            daysOld >= 20 ? 'bg-red-600 text-white' :
            daysOld >= 10 ? 'bg-orange-500 text-white' :
            daysOld >= 2 ? 'bg-yellow-500 text-black' :
            'bg-green-500 text-white'
          }`}>
            {daysOld}
          </div>
        );
      }
    },
    {
      Header: 'Shipment Name',
      accessor: 'NAME',
      Cell: ({ value, row }) => (
        <span className="text-xs font-medium truncate block" title={value}>
          {value}
        </span>
      )
    },
    {
      Header: 'Units',
      accessor: 'ITEM_COUNT',
      width: 90,
      Cell: ({ value, row }) => (
        <span className="text-sm font-mono text-right block font-medium">
          {parseInt(value || 0).toLocaleString()}
        </span>
      )
    },
    {
      Header: 'SKUs',
      accessor: 'SKU_COUNT',
      width: 70,
      Cell: ({ value, row }) => (
        <span className="text-sm font-mono text-center block font-medium">
          {parseInt(value || 0)}
        </span>
      )
    },
    {
      Header: 'Client',
      accessor: 'name',
      width: 160,
      Cell: ({ value, row }) => (
        <span className="text-xs truncate block font-medium" title={value}>
          {value}
        </span>
      )
    },
    {
      Header: 'Notes',
      accessor: 'notes',
      width: 250,
      Cell: ({ value, row }) => {
        // Handle both uppercase NOTES and lowercase notes
        const noteText = value || row.original.NOTES || row.original.notes;
        const daysOld = parseInt(row.original.BusinessDaysOld || 0);
        const isBlackBg = daysOld >= 40;
        
        return (
          <div 
            className={`text-xs truncate cursor-pointer px-1 py-1 rounded ${
              isBlackBg ? 'hover:bg-red-700' : 'hover:bg-gray-50'
            }`}
            title={noteText || 'Click to add notes'}
            onClick={(e) => {
              e.stopPropagation();
              // Open the row expansion to show the notes editor
              if (!expandedRows[row.original.ID]) {
                toggleRowExpansion(row.original.ID);
              }
            }}
          >
            {noteText ? (
              <span className={`px-1 py-0.5 rounded font-medium text-xs ${
                isBlackBg ? 'bg-blue-200 text-blue-900' : 'bg-blue-100 text-blue-900'
              }`}>
                {noteText.length > 30 ? `${noteText.substring(0, 30)}...` : noteText}
              </span>
            ) : (
              <span className={`italic text-xs ${isBlackBg ? 'text-gray-300 hover:text-blue-300' : 'text-gray-500 hover:text-blue-600'}`}>
                Add notes
              </span>
            )}
          </div>
        );
      }
    },
    {
      Header: 'Status',
      accessor: 'STATUS',
      width: 90,
      Cell: ({ value }) => (
        <span className={`px-1 py-0.5 text-xs font-semibold uppercase ${
          value === 'planning' ? 'bg-yellow-200 text-yellow-900' :
          value === 'planned' ? 'bg-blue-200 text-blue-900' :
          'bg-green-200 text-green-900'
        }`}>
          {value?.slice(0, 4)}
        </span>
      )
    },
    {
      Header: 'Actions',
      id: 'actions',
      width: 140,
      Cell: ({ row }) => (
        <div className="flex items-center justify-end space-x-1">
          <a
            href={`${window.location.origin}/outbound/${row.original.ID}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`px-2 py-1 text-xs rounded border ${
              parseInt(row.original.BusinessDaysOld || 0) >= 40 
                ? 'text-blue-200 border-blue-200 hover:text-white hover:bg-red-700' 
                : 'text-blue-600 border-blue-600 hover:text-blue-800 hover:bg-blue-100'
            }`}
            onClick={(e) => e.stopPropagation()}
            title="View box contents"
          >
            Box
          </a>
          <a
            href={`https://app.yyzprep.ca/staff/shipments/outbound/${row.original.ID}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`px-2 py-1 text-xs rounded border ${
              parseInt(row.original.BusinessDaysOld || 0) >= 40 
                ? 'text-blue-200 border-blue-200 hover:text-white hover:bg-red-700' 
                : 'text-blue-600 border-blue-600 hover:text-blue-800 hover:bg-blue-100'
            }`}
            onClick={(e) => e.stopPropagation()}
            title="Open in YYZ Prep"
          >
            YYZ
          </a>
          {activeTab === 'all' && !row.original.ignored && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onIgnore(row.original.ID);
              }}
              className={`px-2 py-1 text-xs rounded border ${
                parseInt(row.original.BusinessDaysOld || 0) >= 40 
                  ? 'text-red-300 border-red-300 hover:text-red-100 hover:bg-red-700' 
                  : 'text-red-600 border-red-600 hover:text-red-800 hover:bg-red-100'
              }`}
              title="Mark as irrelevant"
            >
              Ignore
            </button>
          )}
          {activeTab === 'priority' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemoveFromPriority(row.original.ID);
              }}
              className={`px-2 py-1 text-xs rounded border ${
                parseInt(row.original.BusinessDaysOld || 0) >= 40 
                  ? 'text-red-300 border-red-300 hover:text-red-100 hover:bg-red-700' 
                  : 'text-red-600 border-red-600 hover:text-red-800 hover:bg-red-100'
              }`}
              title="Remove from priority list"
            >
              Remove
            </button>
          )}
        </div>
      )
    }
  ], [editingNotes, onIgnore, onUpdateNotes, activeTab, priorityList, completedItems, expandedRows, onAddToPriority, onRemoveFromPriority, onToggleCompleted]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [{ id: 'BusinessDaysOld', desc: true }]
      }
    },
    useGlobalFilter,
    useSortBy,
    useExpanded
  );

  // Update global filter when search term changes
  React.useEffect(() => {
    setGlobalFilter(searchTerm);
  }, [searchTerm, setGlobalFilter]);

  const getRowBackground = (row) => {
    const daysOld = parseInt(row.original.BusinessDaysOld || 0);
    const isCompleted = activeTab === 'priority' && completedItems.includes(row.original.ID);
    
    if (isCompleted) return 'bg-green-100';
    if (row.original.ignored) return 'bg-gray-100';
    if (daysOld >= 40) return 'bg-red-800 text-white';
    if (daysOld >= 20) return 'bg-red-100';
    if (daysOld >= 10) return 'bg-orange-100';
    if (daysOld >= 2) return 'bg-yellow-100';
    return 'bg-white';
  };

  const getRowTextColor = (row) => {
    const daysOld = parseInt(row.original.BusinessDaysOld || 0);
    if (daysOld >= 40) return 'text-white';
    return 'text-gray-900';
  };

  const ExpandedRowContent = ({ row }) => (
    <tr>
      <td colSpan={columns.length} className="px-3 py-4 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Shipment Details</h4>
            <div className="space-y-1 text-xs">
              <div><span className="font-medium">Full Name:</span> {row.original.NAME}</div>
              <div><span className="font-medium">Client:</span> {row.original.name}</div>
              <div><span className="font-medium">Status:</span> {row.original.STATUS}</div>
              <div><span className="font-medium">Created:</span> {row.original.created_at ? new Date(row.original.created_at).toLocaleDateString() : 'N/A'}</div>
              {activeTab === 'priority' && row.original.addedAt && (
                <div><span className="font-medium">Added to Priority:</span> {new Date(row.original.addedAt).toLocaleDateString()}</div>
              )}
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Notes & Comments</h4>
            <div className="space-y-2">
              <div>
                <label className="text-xs font-medium text-gray-700">Task Notes:</label>
                <NotesEditor 
                  outboundId={row.original.ID}
                  initialNotes={row.original.NOTES || row.original.notes || ''}
                  onSave={onUpdateNotes}
                  onClose={() => toggleRowExpansion(row.original.ID)}
                />
              </div>
              {row.original.internal_notes && (
                <div>
                  <label className="text-xs font-medium text-gray-700">Internal Notes:</label>
                  <div className="mt-1 p-2 bg-yellow-50 border border-yellow-200 text-xs">
                    {row.original.internal_notes}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </td>
    </tr>
  );

  const NotesEditor = ({ outboundId, initialNotes, onSave, onClose }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [noteText, setNoteText] = useState(initialNotes);

    const handleSave = () => {
      onSave(outboundId, noteText);
      setIsEditing(false);
      if (onClose) onClose(); // Close the expanded row
    };

    const handleCancel = () => {
      setNoteText(initialNotes);
      setIsEditing(false);
    };

    if (isEditing) {
      return (
        <div className="mt-1 space-y-2">
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            className="w-full px-2 py-1 text-xs border border-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            rows={3}
            placeholder="Add notes..."
          />
          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              className="px-2 py-1 text-xs bg-blue-600 text-white hover:bg-blue-700"
            >
              Save
            </button>
            <button
              onClick={handleCancel}
              className="px-2 py-1 text-xs bg-gray-300 text-gray-700 hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        onClick={() => setIsEditing(true)}
        className="mt-1 p-2 bg-white border border-gray-200 text-xs cursor-pointer hover:bg-gray-50 min-h-[60px]"
      >
        {noteText || <span className="text-gray-400">Click to add notes...</span>}
      </div>
    );
  };

  return (
    <div className="h-full overflow-auto">
      <div className="min-w-[1200px]">
        <table {...getTableProps()} className="w-full text-sm border border-gray-300">
        <thead className="bg-gray-50 sticky top-0">
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className="px-2 py-1 text-left text-xs font-semibold text-gray-800 uppercase tracking-wider border-b border-r border-gray-300"
                  style={{ width: column.width }}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.render('Header')}</span>
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <span className="text-gray-400">↓</span>
                      ) : (
                        <span className="text-gray-400">↑</span>
                      )
                    ) : null}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);
            return (
              <React.Fragment key={row.getRowProps().key}>
                <tr
                  {...row.getRowProps()}
                  className={`${getRowBackground(row)} ${
                    parseInt(row.original.BusinessDaysOld || 0) >= 40 ? 'hover:bg-red-700' : 'hover:bg-gray-100'
                  } cursor-pointer border-b border-gray-200 ${
                    selectedId === row.original.ID ? 'ring-2 ring-blue-500' : ''
                  }`}
                  onClick={() => onSelect(row.original)}
                >
                  {row.cells.map(cell => (
                    <td
                      {...cell.getCellProps()}
                      className={`px-2 py-1 whitespace-nowrap border-r border-gray-200 ${getRowTextColor(row)}`}
                      style={{ width: cell.column.width }}
                    >
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
                {expandedRows[row.original.ID] && <ExpandedRowContent row={row} />}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default Tasks; 